import { useEffect, useState, useMemo } from "react";
import { VideoData } from "./types";

type VideoLoadingState = {
  isLoaded: boolean;
  isLoading: boolean;
  progress: number;
  totalFrames: number;
  loadedFrames: number;
};

type PreloadStatus = {
  isLoading: boolean;
  globalProgress: number;
  total: number;
  loaded: number;
  preloadedImages: Set<string>;
  videoStates: Record<number, VideoLoadingState>;
  firstFrameLoaded: boolean;
};

const initialStatus: PreloadStatus = {
  isLoading: false,
  globalProgress: 0,
  total: 0,
  loaded: 0,
  preloadedImages: new Set<string>(),
  videoStates: {},
  firstFrameLoaded: false,
};

// Preloads images for video frames
export function useImagePreloader(videoData: VideoData | null, framesPath: string) {
  const [status, setStatus] = useState<PreloadStatus>(initialStatus);

  // Extract frame data organized by video segments for priority loading
  const frameData = useMemo(() => {
    if (!videoData) return { allFrames: [], videoFrameMap: {}, priorityFrames: [] };

    const uniqueFrames = new Set<string>();
    const videoFrameMap: Record<number, string[]> = {};
    const { framePrefix, frameDigits, frameExtension } = videoData.metadata;

    // Generate frames for each video and track which frames belong to which video
    videoData.videos.forEach((video, videoIndex) => {
      const startFrameNum = parseInt(video.startFrame.match(/\d+/)?.[0] || "0");
      const endFrameNum = parseInt(video.endFrame.match(/\d+/)?.[0] || "0");
      const videoFrames: string[] = [];

      // Handle potential wrap-around or invalid frame ranges
      if (startFrameNum <= endFrameNum) {
        for (let i = startFrameNum; i <= endFrameNum; i++) {
          const frameName = `${framePrefix}${i.toString().padStart(frameDigits, "0")}${frameExtension}`;
          uniqueFrames.add(frameName);
          videoFrames.push(frameName);
        }
      } else {
        // Handle wrap-around case
        for (let i = startFrameNum; i <= videoData.metadata.totalFrames; i++) {
          const frameName = `${framePrefix}${i.toString().padStart(frameDigits, "0")}${frameExtension}`;
          uniqueFrames.add(frameName);
          videoFrames.push(frameName);
        }
        for (let i = 1; i <= endFrameNum; i++) {
          const frameName = `${framePrefix}${i.toString().padStart(frameDigits, "0")}${frameExtension}`;
          uniqueFrames.add(frameName);
          videoFrames.push(frameName);
        }
      }
      
      videoFrameMap[videoIndex] = videoFrames;
    });

    // Priority frames: first frame of each video (for immediate navigation)
    const priorityFrames = videoData.videos.map(video => video.startFrame);

    return {
      allFrames: Array.from(uniqueFrames),
      videoFrameMap,
      priorityFrames
    };
  }, [videoData]);

  // Debug logging for frame data
  useEffect(() => {
    if (frameData.allFrames.length > 0) {
      console.log(
        `[Preloader Hook] Generated ${frameData.allFrames.length} frame names for ${Object.keys(frameData.videoFrameMap).length} videos`,
        { priorityFrames: frameData.priorityFrames, totalVideos: Object.keys(frameData.videoFrameMap).length }
      );
    }
  }, [frameData]);

  useEffect(() => {
    if (!videoData || frameData.allFrames.length === 0) {
      setStatus(initialStatus);
      return;
    }

    let worker: Worker | null = null;
    const preloadedImages = new Set<string>();
    
    // Initialize video states
    const initialVideoStates: Record<number, VideoLoadingState> = {};
    Object.keys(frameData.videoFrameMap).forEach(videoIndex => {
      const videoIdx = parseInt(videoIndex);
      initialVideoStates[videoIdx] = {
        isLoaded: false,
        isLoading: true,
        progress: 0,
        totalFrames: frameData.videoFrameMap[videoIdx].length,
        loadedFrames: 0
      };
    });

    try {
      worker = new Worker(new URL("./imagePreloader.worker.ts", import.meta.url), { type: "module" });
      console.log(`[Preloader Hook] Worker created for ${frameData.allFrames.length} frames with priority loading`);

      setStatus({
        isLoading: true,
        globalProgress: 0,
        total: frameData.allFrames.length,
        loaded: 0,
        preloadedImages,
        videoStates: initialVideoStates,
        firstFrameLoaded: false,
      });

      worker.addEventListener("message", (event) => {
        const { type, payload } = event.data;

        if (type === "PROGRESS") {
          const { loaded, total, loadedImage, failedImage, videoIndex } = payload;

          if (loadedImage) {
            preloadedImages.add(loadedImage);
          }

          setStatus(prevStatus => {
            const newVideoStates = { ...prevStatus.videoStates };
            
            // Update specific video state if videoIndex is provided
            if (videoIndex !== undefined && newVideoStates[videoIndex]) {
              const videoFrames = frameData.videoFrameMap[videoIndex];
              const loadedFramesForVideo = videoFrames.filter(frame => preloadedImages.has(frame)).length;
              
              newVideoStates[videoIndex] = {
                ...newVideoStates[videoIndex],
                loadedFrames: loadedFramesForVideo,
                progress: Math.round((loadedFramesForVideo / videoFrames.length) * 100),
                isLoaded: loadedFramesForVideo === videoFrames.length,
                isLoading: loadedFramesForVideo < videoFrames.length
              };
            }

            const isFirstFrame = loadedImage === videoData.videos[0]?.startFrame;
            
            return {
              ...prevStatus,
              isLoading: loaded < total,
              globalProgress: Math.round((loaded / total) * 100),
              total,
              loaded,
              preloadedImages: new Set(preloadedImages),
              videoStates: newVideoStates,
              firstFrameLoaded: prevStatus.firstFrameLoaded || isFirstFrame
            };
          });

          if (failedImage) {
            console.warn(`[Preloader Hook] Failed to load: ${failedImage}`);
          }
        } else if (type === "COMPLETE") {
          console.log(`[Preloader Hook] Loading complete!`, payload);
          setStatus(prevStatus => ({
            ...prevStatus,
            isLoading: false,
            globalProgress: 100,
            total: payload.total,
            loaded: payload.total,
            preloadedImages: new Set(preloadedImages),
            videoStates: Object.fromEntries(
              Object.entries(prevStatus.videoStates).map(([key, state]) => [
                key, 
                { ...state, isLoaded: true, isLoading: false, progress: 100 }
              ])
            )
          }));
        }
      });

      worker.addEventListener("error", (error) => {
        console.error("Image preloader worker error:", error);
        setStatus({
          isLoading: false,
          globalProgress: 0,
          total: frameData.allFrames.length,
          loaded: 0,
          preloadedImages: new Set(),
          videoStates: {},
          firstFrameLoaded: false,
        });
      });

      worker.postMessage({
        type: "PRELOAD_IMAGES",
        payload: {
          framesPath,
          frameNames: frameData.allFrames,
          priorityFrames: frameData.priorityFrames,
          videoFrameMap: frameData.videoFrameMap,
          maxConcurrent: 20 // Increased from 10 for better performance
        },
      });

      console.log(`[Preloader Hook] Sent PRELOAD_IMAGES message with ${frameData.allFrames.length} frames and ${frameData.priorityFrames.length} priority frames`);
    } catch (error) {
      console.error("Error initializing image preloader worker:", error);
      setStatus(initialStatus);
    }

    return () => {
      if (worker) {
        console.log(`[Preloader Hook] Terminating worker`);
        worker.terminate();
      }
    };
  }, [videoData, framesPath, frameData]);

  return status;
}

// Helper function to check if a specific video can be navigated to
export function isVideoNavigable(status: PreloadStatus, videoIndex: number): boolean {
  const videoState = status.videoStates[videoIndex];
  if (!videoState) return false;
  
  // A video is navigable if at least its start frame is loaded
  return videoState.loadedFrames > 0;
}

// Helper function to get video loading state
export function getVideoLoadingState(status: PreloadStatus, videoIndex: number): VideoLoadingState | null {
  return status.videoStates[videoIndex] || null;
}
